// Visualización / descarga de los documentos adjuntos del reclamo desde el resumen
// público. El backend devuelve el objeto en base64 ({ buffer, contentType }); aquí se
// reconstruye un Blob con el MIME correcto para previsualizar inline o descargar. Se
// PREFIERE el `contentType` del backend (derivado de la key o del Content-Type real de
// S3), así funciona aunque el filename no traiga extensión (p. ej. un UUID).

// MIME por extensión (fallback si el backend no entrega contentType). Incluye video.
const contentTypeMap: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',
  webm: 'video/webm',
  m4v: 'video/x-m4v',
  '3gp': 'video/3gpp',
  mkv: 'video/x-matroska',
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'jfif', 'gif', 'webp', 'bmp', 'tiff'];

// Extensión en minúsculas; '' si el nombre no tiene punto (evita tratar un UUID como extensión).
export const extOf = (filename?: string): string =>
  filename && filename.includes('.') ? (filename.split('.').pop() || '').toLowerCase() : '';

// ¿Es imagen? Por contentType (preferido) o por extensión del filename.
export const isImageFile = (filename?: string, contentType?: string): boolean =>
  (contentType ? contentType.startsWith('image/') : false) || IMAGE_EXTENSIONS.includes(extOf(filename));

export const mimeOf = (filename?: string, contentType?: string): string =>
  contentType || contentTypeMap[extOf(filename)] || 'application/octet-stream';

export const base64ToBlob = (base64: string, filename?: string, contentType?: string): Blob => {
  const byteChars = atob(base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mimeOf(filename, contentType) });
};

// Nombre de descarga: usa el nombre del documento y le añade la extensión si falta.
export const downloadNameOf = (doc: { documentName?: string; filename?: string }): string => {
  const ext = extOf(doc.filename);
  const baseName = doc.documentName || doc.filename || 'documento';
  return ext && !baseName.toLowerCase().endsWith(`.${ext}`) ? `${baseName}.${ext}` : baseName;
};
